import React, { useState, useCallback } from 'react';

/**
 * McaTabs — Tab navigation with underline style.
 * Manages tab selection state and content display.
 * 
 * Props:
 *   - tabs (array): Array of { id, label } objects
 *   - defaultTab (string): Initial active tab ID
 *   - onChange (fn): Called with tab id when selection changes
 *   - children (fn): Render function receiving { activeTab }. Shows/hides content per tab.
 */
export function McaTabs({ tabs, defaultTab, onChange, children }) {
  const [activeTab, setActiveTab] = useState(defaultTab || tabs?.[0]?.id);

  const handleTabClick = useCallback((tabId) => {
    setActiveTab(tabId);
    onChange?.(tabId);
  }, [onChange]);

  return (
    <div>
      <div className="mca-tabs" role="tablist">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            className="mca-tab"
            role="tab"
            aria-selected={activeTab === tab.id}
            aria-controls={`panel-${tab.id}`}
            onClick={() => handleTabClick(tab.id)}
            type="button"
          >
            {tab.label}
          </button>
        ))}
      </div>
      {children({ activeTab })}
    </div>
  );
}

/**
 * McaSegmentedControl — Pill-style segmented selector (used for filters, view modes, etc.).
 * Looks like .mca-seg in the design.
 * 
 * Props:
 *   - options (array): Array of { id, label } objects
 *   - value (string): Currently selected option id
 *   - onChange (fn): Called with selected option id
 *   - size (str): 'sm' or default (md)
 */
export function McaSegmentedControl({ options, value, onChange, size = 'md' }) {
  return (
    <div className="mca-seg" data-tabset>
      {options.map((option) => (
        <button
          key={option.id}
          className="mca-seg__btn"
          data-seg-btn
          data-target={option.id}
          aria-selected={value === option.id}
          onClick={() => onChange?.(option.id)}
          type="button"
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

export default McaTabs;
